<template>
  <div class="recording-analysis">
    <h2>录音解析功能</h2>
    
    <!-- 录音上传和纪要生成区域 -->
    <div class="upload-section">
      <h3>音频文件上传并生成会议纪要</h3>
      <div class="file-input">
        <input type="file" ref="audioFileInput" accept="audio/*" @change="onFileSelected">
        <p><small>支持格式: MP3, WAV, M4A, AAC</small></p>
      </div>
      <div class="controls">
        <button @click="generateMeetingSummary" :disabled="!selectedFile || processing" class="btn btn-primary">
          {{ processing ? '正在处理...' : '生成会议纪要' }}
        </button>
        <button @click="clearResults" class="btn btn-warning" :disabled="processing">清空结果</button>
      </div>
      <div :class="['status', statusClass]">{{ status }}</div>
      <div class="result-box">
        <div class="result-section" v-if="transcript">
          <h4>转写内容</h4>
          <div class="transcript">{{ transcript }}</div>
        </div>
        <div class="result-section" v-if="summary">
          <h4>会议纪要</h4>
          <div class="transcript">{{ summary }}</div>
        </div>
      </div>
    </div>
    
    <!-- 录音列表 -->
    <div class="recording-list-section">
      <h3>录音列表</h3>
      <div v-if="loading" class="loading">加载中...</div>
      <div v-else-if="recordings.length === 0" class="no-data">暂无录音记录</div>
      <div v-else class="recordings-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>标题</th>
              <th>时长</th>
              <th>状态</th>
              <th>日期</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in recordings" :key="record.id">
              <td>{{ record.id }}</td>
              <td>{{ record.title }}</td>
              <td>{{ formatDuration(record.duration) }}</td>
              <td>{{ formatStatus(record.status) }}</td>
              <td>{{ record.date }}</td>
              <td>
                <button @click="viewDetail(record.id)" class="btn-sm btn-primary">查看</button>
                <button @click="deleteRecording(record.id)" class="btn-sm btn-danger">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- 详情模态框 -->
    <div v-if="showDetailModal" class="modal-overlay" @click="closeDetailModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>录音详情</h3>
          <button @click="closeDetailModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="detail-item">
            <label>标题:</label>
            <div class="detail-value">{{ currentDetail.title || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>转写内容:</label>
            <div class="detail-text-box">
              <div class="copy-btn" @click="copyToClipboard('transcript')" title="复制转写内容">
                📋 复制
              </div>
              <div class="detail-text" ref="transcriptText">
                {{ currentDetail.transcript || '暂无转写内容' }}
              </div>
            </div>
          </div>
          <div class="detail-item">
            <label>会议纪要:</label>
            <div class="detail-text-box">
              <div class="copy-btn" @click="copyToClipboard('summary')" title="复制会议纪要">
                📋 复制
              </div>
              <div class="detail-text" ref="summaryText">
                {{ currentDetail.summary || '暂无会议纪要' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { recordingService } from '../services/recordingService'

export default {
  name: 'RecordingAnalysis',
  data() {
    return {
      recordings: [],
      loading: false,
      processing: false,
      
      // 文件上传和结果展示
      selectedFile: null,
      status: '准备上传文件',
      statusClass: 'stopped',
      transcript: '',
      summary: '',
      
      // 详情模态框
      showDetailModal: false,
      currentDetail: {}
    }
  },
  mounted() {
    this.loadRecordingList()
  },
  methods: {
    // 加载录音列表
    async loadRecordingList() {
      this.loading = true
      try {
        const response = await recordingService.getRecordingList()
        if (response.success) {
          this.recordings = response.data.records || []
        }
      } catch (error) {
        console.error('加载录音列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    // 查看详情
    async viewDetail(id) {
      try {
        const response = await recordingService.getRecordingDetail(id)
        if (response.success) {
          this.currentDetail = response.data
          this.showDetailModal = true
        }
      } catch (error) {
        console.error('获取录音详情失败:', error)
        alert('获取录音详情失败')
      }
    },
    
    // 关闭详情模态框
    closeDetailModal() {
      this.showDetailModal = false
      this.currentDetail = {}
    },
    
    // 复制到剪贴板
    async copyToClipboard(type) {
      try {
        let text = ''
        if (type === 'transcript') {
          text = this.currentDetail.transcript || ''
        } else if (type === 'summary') {
          text = this.currentDetail.summary || ''
        }
        
        if (!text) {
          alert('没有可复制的内容')
          return
        }
        
        await navigator.clipboard.writeText(text)
        alert('已复制到剪贴板')
      } catch (error) {
        console.error('复制失败:', error)
        // 降级方案：使用传统复制方法
        const textarea = document.createElement('textarea')
        textarea.value = type === 'transcript' ? this.currentDetail.transcript : this.currentDetail.summary
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        alert('已复制到剪贴板')
      }
    },
    
    // 删除录音
    async deleteRecording(id) {
      if (!confirm('确定要删除这条录音记录吗？')) {
        return
      }
      try {
        await recordingService.deleteRecording(id)
        this.loadRecordingList()
        alert('删除成功')
      } catch (error) {
        console.error('删除录音失败:', error)
        alert('删除录音失败')
      }
    },
    
    // 文件选择
    onFileSelected(event) {
      this.selectedFile = event.target.files[0]
    },
    
    // 生成会议纪要（自动执行上传->转写->生成纪要）
    async generateMeetingSummary() {
      if (!this.selectedFile) {
        alert('请先选择一个音频文件')
        return
      }
      
      this.processing = true
      this.status = '正在上传文件...'
      this.statusClass = 'connecting'
      this.transcript = ''
      this.summary = ''
      
      try {
        // 1. 上传文件
        const uploadResponse = await recordingService.uploadRecording(this.selectedFile, 0)
        if (!uploadResponse.success) {
          throw new Error('上传失败')
        }
        
        const recordId = uploadResponse.data.recordId
        this.status = '正在转写...'
        
        // 2. 转写
        const transcribeResponse = await recordingService.transcribeRecording(recordId)
        if (!transcribeResponse.success) {
          throw new Error('转写失败')
        }
        
        this.transcript = transcribeResponse.data.transcript
        this.status = '正在生成会议纪要...'
        
        // 3. 生成纪要
        const summaryResponse = await recordingService.generateSummary(recordId)
        if (!summaryResponse.success) {
          throw new Error('生成纪要失败')
        }
        
        this.summary = summaryResponse.data.summary || '(无内容)'
        this.status = '处理完成'
        this.statusClass = 'stopped'
        
        // 刷新列表
        this.loadRecordingList()
      } catch (error) {
        console.error('处理失败:', error)
        this.status = `处理失败: ${error.message}`
        this.statusClass = 'stopped'
        this.transcript = ''
        this.summary = ''
        alert('处理失败，请重试')
      } finally {
        this.processing = false
      }
    },
    
    // 清空结果
    clearResults() {
      this.transcript = ''
      this.summary = ''
      this.status = '准备上传文件'
      this.statusClass = 'stopped'
      this.selectedFile = null
      if (this.$refs.audioFileInput) {
        this.$refs.audioFileInput.value = ''
      }
    },
    
    // 格式化时长
    formatDuration(seconds) {
      if (!seconds || seconds === 0) {
        return '0秒'
      }
      
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60
      
      let result = []
      
      if (hours > 0) {
        result.push(`${hours}小时`)
      }
      if (minutes > 0) {
        result.push(`${minutes}分钟`)
      }
      if (secs > 0 || result.length === 0) {
        result.push(`${secs}秒`)
      }
      
      return result.join('')
    },
    
    // 格式化状态
    formatStatus(status) {
      const statusMap = {
        'uploaded': '已上传',
        'transcribing': '转写中',
        'transcribed': '已转写',
        'summarized': '已生成会议纪要'
      }
      return statusMap[status] || status
    }
  }
}
</script>

<style scoped>
.recording-analysis {
  padding: 20px;
}

h2 {
  color: #333;
  margin-bottom: 30px;
}

h3 {
  color: #555;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  margin-top: 30px;
}

h4 {
  color: #666;
  margin-bottom: 10px;
  font-size: 16px;
}

.loading, .no-data {
  padding: 20px;
  text-align: center;
  color: #666;
}

.upload-section {
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #dee2e6;
  margin-bottom: 30px;
}

.recording-list-section {
  margin-bottom: 40px;
}

.recordings-table {
  overflow-x: auto;
}

.recordings-table table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.recordings-table th,
.recordings-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.recordings-table th {
  background-color: #f8f9fa;
  font-weight: bold;
}

.controls {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-warning {
  background-color: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background-color: #e0a800;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

.status {
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-weight: bold;
}

.status.stopped {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status.connecting {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.result-box {
  margin-top: 20px;
}

.result-section {
  margin-bottom: 20px;
}

.result-section:last-child {
  margin-bottom: 0;
}

.transcript {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 15px;
  min-height: 100px;
  white-space: pre-wrap;
  font-family: 'Courier New', monospace;
  max-height: 400px;
  overflow-y: auto;
}

.file-input {
  margin-bottom: 20px;
}

.file-input input[type="file"] {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 100%;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.close-btn:hover {
  background-color: #e9ecef;
  color: #333;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.detail-item {
  margin-bottom: 25px;
}

.detail-item label {
  display: block;
  font-weight: bold;
  color: #555;
  margin-bottom: 8px;
  font-size: 16px;
}

.detail-value {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  color: #333;
  font-size: 14px;
}

.detail-text-box {
  position: relative;
}

.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
}

.copy-btn:hover {
  background-color: #0056b3;
}

.detail-text {
  padding: 15px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}
</style>